import { Box, Button, Divider, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { InfoIcon, MarketAnalysisIcon, PencilExtraIcon, PropertyIcon } from "@/assets";
import { CustomChip } from "@/components/customChip/CustomChip";
import { EnhancedTooltip } from "@/components/enhancedTooltip";
import { PanelHeader } from "@/components/panelHeader";
import { colors } from "@/theme/tokens/colors";

export const ValuationReportSkeleton = (): JSX.Element => {
  const { t } = useTranslation("ValuationReport");
  const { t: tMarketAnalysis } = useTranslation("MarketAnalysisPanel");
  const theme = useTheme();

  const styles = {
    pageGrid: {
      height: "fit-content",
      display: "grid",
      gridTemplateRows: "auto 1fr",
      gap: "30px",
      padding: "76px 10px 10px 10px",
    },
    valuationDetailsContainer: {
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        md: "1fr 1fr",
      },
      gap: "30px",
    },
    panelSkeleton: {
      backgroundColor: "white",
      padding: "30px",
      display: "flex",
      flexDirection: "column",
      gap: "30px",
    },
    marketAnalysisPanelContainer: {
      display: "grid",
      backgroundColor: "white",
      padding: "30px",
      gridTemplateRows: "min-content 1fr min-content 1fr min-content 1fr",
    },
    priceContainer: {
      marginTop: "30px",
      backgroundColor: theme.semanticColors.surface.blueBg,
      borderLeft: `3px solid ${theme.palette.primary.main}`,
    },
    responsiveContainer: {
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        md: "1fr",
        lg: "1fr 1fr",
      },
    },
    priceSectionContainer: {
      display: "flex",
      flexFlow: "column",
      justifyContent: "space-evenly",
      padding: "14px 20px",
    },
    infoIcon: {
      ml: 1,
      fontSize: "24px",
      cursor: "pointer",
      color: "primary.main",
    },
  };

  return (
    <Box sx={styles.pageGrid}>
      <Box sx={styles.valuationDetailsContainer}>
        {/* RealEstateDataPanel Skeleton with Real Header */}
        <Box sx={styles.panelSkeleton}>
          <PanelHeader
            title={t("propertyData.propertyData")}
            description={t("propertyData.propertyDataDescription")}
            icon={<PropertyIcon sx={{ fontSize: "30px", color: colors.brand.brownDark }} />}
          />

          {/* Address Skeleton */}
          <Stack spacing={1}>
            <Skeleton variant="text" width="70%" height={24} animation="wave" />
            <Skeleton variant="text" width="50%" height={20} animation="wave" />
          </Stack>

          {/* Property Details Grid */}
          <Stack direction="row" spacing={3}>
            <Stack spacing={2}>
              <Skeleton variant="text" width={80} height={20} animation="wave" />
              <Skeleton variant="text" width={120} height={16} animation="wave" />
            </Stack>
            <Stack spacing={2}>
              <Skeleton variant="text" width={100} height={20} animation="wave" />
              <Skeleton variant="text" width={90} height={16} animation="wave" />
            </Stack>
            <Stack spacing={2}>
              <Skeleton variant="text" width={60} height={20} animation="wave" />
              <Skeleton variant="text" width={80} height={16} animation="wave" />
            </Stack>
          </Stack>

          {/* Map and Image Skeletons */}
          <Stack direction="row" spacing={3}>
            <Box flex={3}>
              <Skeleton variant="rounded" height={120} animation="wave" />
            </Box>
            <Box flex={2}>
              <Skeleton variant="rounded" height={120} animation="wave" />
            </Box>
          </Stack>

          {/* Footer with skeleton indicator and real static content */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={"15px"}
            sx={{
              paddingTop: "25px",
              borderTop: `1px solid ${theme.palette.primary.main}`,
              justifyContent: "space-between",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={"15px"}>
              <Skeleton
                variant="circular"
                width={55}
                height={55}
                animation="wave"
                sx={{ borderRadius: "50%", flexShrink: 0 }}
              />
              <Typography
                sx={{
                  fontWeight: "400",
                  fontSize: "14px",
                  lineHeight: "19px",
                  fontFamily: "Open Sans",
                  color: theme.palette.text.secondary,
                }}
              >
                {t("propertyData.optimizeAccuracy")}
              </Typography>
            </Stack>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PencilExtraIcon />}
              disabled
              sx={{ minWidth: "180px", height: "fit-content" }}
            >
              {t("propertyData.refineData")}
            </Button>
          </Stack>
        </Box>

        {/* MarketAnalysisPanel Skeleton with Exact Layout */}
        <Box sx={styles.marketAnalysisPanelContainer}>
          <PanelHeader
            title={tMarketAnalysis("marketAnalysis")}
            description={tMarketAnalysis("marketAnalysisDescription")}
            icon={<MarketAnalysisIcon sx={{ fontSize: "45px", color: colors.brand.brownDark }} />}
          />

          {/* Asking Price Section */}
          <Box sx={styles.priceContainer}>
            <Box sx={{ ...styles.priceSectionContainer, height: "100%" }}>
              <Stack direction={"row"} alignItems={"center"} spacing={"15px"}>
                <Typography variant={"inputLabel"}>{tMarketAnalysis("askingPrice")}</Typography>
                <CustomChip label={tMarketAnalysis("yourPrice")} chipType="primary" />
              </Stack>
              <Stack direction={"row"} alignItems={"baseline"} spacing={"15px"}>
                <Skeleton variant="text" width={200} height={40} animation="wave" />
                <Skeleton variant="text" width={100} height={18} animation="wave" />
              </Stack>
            </Box>
          </Box>

          <Divider />

          {/* Market Price Section */}
          <Box sx={styles.responsiveContainer}>
            <Box sx={styles.priceSectionContainer}>
              <Stack alignItems={"start"} spacing={"14px"} direction="row">
                <Stack alignItems={"start"} direction="row">
                  <Typography variant={"inputLabel"}>{tMarketAnalysis("marketPrice")}</Typography>
                  <EnhancedTooltip
                    header={tMarketAnalysis("marketPriceInfo.header")}
                    content={tMarketAnalysis("marketPriceInfo.content")}
                  >
                    <Box sx={{ display: "flex" }}>
                      <InfoIcon sx={styles.infoIcon} />
                    </Box>
                  </EnhancedTooltip>
                </Stack>
                <Skeleton variant="rounded" width={60} height={24} animation="wave" />
              </Stack>

              <Stack direction={"row"} alignItems={"baseline"} spacing={"15px"}>
                <Skeleton variant="text" width={180} height={40} animation="wave" />
                <Skeleton variant="text" width={100} height={18} animation="wave" />
              </Stack>

              <Typography variant="panelDescription">{tMarketAnalysis("marketPriceCompliance")}</Typography>
            </Box>
          </Box>

          <Divider />

          {/* Price Range Section */}
          <Box sx={styles.priceSectionContainer}>
            <Stack alignItems={"start"} spacing={"14px"} direction="row">
              <Stack alignItems={"start"} direction="row">
                <Typography variant={"inputLabel"}>{tMarketAnalysis("marketPriceRange")}</Typography>
                <EnhancedTooltip
                  header={tMarketAnalysis("priceRangeInfo.header")}
                  content={tMarketAnalysis("priceRangeInfo.content")}
                >
                  <Box sx={{ display: "flex" }}>
                    <InfoIcon sx={styles.infoIcon} />
                  </Box>
                </EnhancedTooltip>
              </Stack>
              <Skeleton variant="rounded" width={70} height={24} animation="wave" />
            </Stack>

            <Stack direction={"row"} alignItems={"baseline"} spacing={"15px"}>
              <Skeleton variant="text" width={250} height={40} animation="wave" />
              <Skeleton variant="text" width={120} height={18} animation="wave" />
            </Stack>

            <Typography variant="panelDescription">{tMarketAnalysis("priceWithinRangeInfo")}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
